import { deleteDB, openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { V2Operation, V2SyncResponse } from '@holdthat/api';
import { asBytes, type AttachmentManifestV1, type Binary, type EncryptedEntityV1, type GroupKeyGrantV1, type KeyEnvelopeV1 } from '@holdthat/crypto';

export interface CiphertextOutboxRecord {
  operationID: string; createdAt: string; attempts: number; lastError?: string; operation: V2Operation;
}

interface EncryptedDatabase extends DBSchema {
  entities: { key: string; value: EncryptedEntityV1; indexes: { 'by-type': string; 'by-created': string } };
  envelopes: { key: string; value: KeyEnvelopeV1; indexes: { 'by-entity': string } };
  grants: { key: string; value: GroupKeyGrantV1; indexes: { 'by-group': string } };
  manifests: { key: string; value: AttachmentManifestV1; indexes: { 'by-hold': string } };
  ciphertextBlocks: { key: string; value: Blob };
  outbox: { key: string; value: CiphertextOutboxRecord; indexes: { 'by-created': string } };
  meta: { key: string; value: unknown };
}

export class EncryptedLocalRepository {
  private database?: IDBPDatabase<EncryptedDatabase>;
  constructor(public readonly userID: string, private readonly namespace = 'web') {}
  private get name() { return `holdthat-e2ee-${this.namespace}-${this.userID.replace(/[^a-zA-Z0-9_-]/g, '_')}`; }
  private async db() {
    this.database ??= await openDB<EncryptedDatabase>(this.name, 1, {
      upgrade(database) {
        const entities = database.createObjectStore('entities', { keyPath: 'entityID' });
        entities.createIndex('by-type', 'entityType'); entities.createIndex('by-created', 'createdAt');
        const envelopes = database.createObjectStore('envelopes', { keyPath: 'keyEnvelopeID' }); envelopes.createIndex('by-entity', 'entityID');
        const grants = database.createObjectStore('grants', { keyPath: 'grantID' }); grants.createIndex('by-group', 'groupID');
        const manifests = database.createObjectStore('manifests', { keyPath: 'attachmentID' }); manifests.createIndex('by-hold', 'holdID');
        database.createObjectStore('ciphertextBlocks');
        const outbox = database.createObjectStore('outbox', { keyPath: 'operationID' }); outbox.createIndex('by-created', 'createdAt');
        database.createObjectStore('meta');
      },
    });
    return this.database;
  }
  async entities(type?: string) { const db = await this.db(); return type ? db.getAllFromIndex('entities', 'by-type', type) : db.getAll('entities'); }
  async entity(id: string) { return (await this.db()).get('entities', id); }
  async envelopes(entityID?: string) { const db = await this.db(); return entityID ? db.getAllFromIndex('envelopes', 'by-entity', entityID) : db.getAll('envelopes'); }
  async grants(groupID?: string) { const db = await this.db(); return groupID ? db.getAllFromIndex('grants', 'by-group', groupID) : db.getAll('grants'); }
  async manifests(holdID?: string) { const db = await this.db(); return holdID ? db.getAllFromIndex('manifests', 'by-hold', holdID) : db.getAll('manifests'); }
  async manifest(attachmentID: string) { return (await this.db()).get('manifests', attachmentID); }
  async outbox() { return (await this.db()).getAllFromIndex('outbox', 'by-created'); }
  async meta<T>(key: string) { return (await this.db()).get('meta', key) as Promise<T | undefined>; }
  async setMeta(key: string, value: unknown) { await (await this.db()).put('meta', value, key); }
  async queue(operation: V2Operation) {
    const database = await this.db(); const transaction = database.transaction(['entities', 'envelopes', 'outbox'], 'readwrite');
    if (operation.kind === 'upsertEntity') {
      await transaction.objectStore('entities').put(operation.entity);
      for (const envelope of operation.keyEnvelopes) await transaction.objectStore('envelopes').put(envelope);
    }
    await transaction.objectStore('outbox').put({ operationID: operation.operationID, operation, createdAt: new Date().toISOString(), attempts: 0 });
    await transaction.done;
  }
  async saveEntity(entity: EncryptedEntityV1, envelopes: KeyEnvelopeV1[]) {
    const transaction = (await this.db()).transaction(['entities', 'envelopes'], 'readwrite');
    await transaction.objectStore('entities').put(entity);
    for (const envelope of envelopes) await transaction.objectStore('envelopes').put(envelope);
    await transaction.done;
  }
  async confirmOperations(operationIDs: string[]) {
    const transaction = (await this.db()).transaction('outbox', 'readwrite');
    for (const id of operationIDs) await transaction.store.delete(id); await transaction.done;
  }
  async recordFailure(record: CiphertextOutboxRecord, error: unknown) {
    await (await this.db()).put('outbox', { ...record, attempts: record.attempts + 1, lastError: error instanceof Error ? error.message : String(error) });
  }
  async mergeSync(snapshot: V2SyncResponse) {
    const database = await this.db(); const transaction = database.transaction(['entities', 'envelopes', 'grants', 'meta'], 'readwrite');
    for (const entity of snapshot.entities) {
      const existing = await transaction.objectStore('entities').get(entity.entityID);
      if (!existing || entity.revision >= existing.revision) await transaction.objectStore('entities').put(entity);
    }
    for (const tombstone of snapshot.tombstones) await transaction.objectStore('entities').delete(tombstone.entityID);
    for (const envelope of snapshot.keyEnvelopes) await transaction.objectStore('envelopes').put(envelope);
    for (const grant of snapshot.groupKeyGrants) await transaction.objectStore('grants').put(grant);
    await transaction.objectStore('meta').put(snapshot.cursor, 'syncCursor');
    await transaction.objectStore('meta').put(snapshot.relations, 'relations');
    await transaction.objectStore('meta').put(snapshot.memberships, 'memberships');
    await transaction.objectStore('meta').put(snapshot.groups, 'groupSnapshots');
    await transaction.objectStore('meta').put(snapshot.notifications, 'notifications');
    await transaction.done;
  }
  async saveManifest(manifest: AttachmentManifestV1) { await (await this.db()).put('manifests', manifest); }
  async queueAttachment(manifest: AttachmentManifestV1) {
    await this.saveManifest(manifest);
    const pending = new Set(await this.meta<string[]>('pendingAttachments') ?? []); pending.add(manifest.attachmentID);
    await this.setMeta('pendingAttachments', [...pending]);
  }
  async pendingAttachments() { return this.meta<string[]>('pendingAttachments').then((value) => value ?? []); }
  async confirmAttachment(attachmentID: string) {
    await this.setMeta('pendingAttachments', (await this.pendingAttachments()).filter((id) => id !== attachmentID));
  }
  async saveCiphertextBlock(attachmentID: string, revision: number, index: number, bytes: Binary) { await (await this.db()).put('ciphertextBlocks', new Blob([asBytes(bytes)]), blockKey(attachmentID, revision, index)); }
  async ciphertextBlock(attachmentID: string, revision: number, index: number) { return (await this.db()).get('ciphertextBlocks', blockKey(attachmentID, revision, index)); }
  async deleteCiphertextBlocks(attachmentID: string, revision: number, count: number) {
    const transaction = (await this.db()).transaction('ciphertextBlocks', 'readwrite');
    for (let index = 0; index < count; index++) await transaction.store.delete(blockKey(attachmentID, revision, index)); await transaction.done;
  }
  close() { this.database?.close(); this.database = undefined; }
  async destroy() { this.close(); await deleteDB(this.name); }
}

export function blockKey(attachmentID: string, revision: number, index: number) { return `${attachmentID}:${revision}:${index}`; }

export async function deleteLegacyRepository(userID: string) {
  await deleteDB(`holdthat-web-${userID.replace(/[^a-zA-Z0-9_-]/g, '_')}`);
}
